// Copyright 2026 Monterosa Productions Limited. All rights reserved.

import { configure } from '@monterosa/sdk-core';
import {
  getExperience,
  embed as sdkEmbed,
  unmount as sdkUnmount,
  sendMessage,
  onMessage,
} from '@monterosa/sdk-launcher-kit';
import {
  getIdentify,
  setCredentials,
  onLoginRequestedByExperience,
} from '@monterosa/sdk-identify-kit';
import { login, silentRefresh, userManager } from './auth.js';
import { getConsent, onConsentChange } from './consent.js';
import { openPreferences } from './onetrust.js';
import { config } from './config.js';
import type { ConsentCategory } from './protocol.js';

const sdk = configure({ host: config.sdkHost, projectId: config.sdkProjectId });

const experience = getExperience(sdk, {
  experienceUrl: config.embedUrl,
  hidesHeadersAndFooters: true,
  supportsLoadingState: false,
  parameters: {
    embed: 'web'
  }
});

const identify = getIdentify(sdk);

let analyticsConsent = getConsent()?.analytics ?? false;
let ga4TrackingId: string | null = null;
let ga4Initialized = false;
let analyticsDebug = false;

function initGA4(trackingId: string): void {
  const s = document.createElement('script');
  s.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  s.async = true;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer ?? [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', trackingId);
}

// Load GA4 only once a tracking ID is configured AND the user has granted
// analytics consent. Safe to call repeatedly — it self-guards.
function ensureGA4(): void {
  if (ga4Initialized || !ga4TrackingId || !analyticsConsent) return;
  initGA4(ga4TrackingId);
  ga4Initialized = true;
  if (analyticsDebug) console.log('[analytics] GA4 initialised:', ga4TrackingId);
}

// Called by the boot sequence once the GA Tracking ID is read from project
// settings. Initialises GA4 immediately if consent was already granted.
export function enableAnalytics(trackingId: string, debug = false): void {
  ga4TrackingId = trackingId;
  analyticsDebug = debug;
  if (analyticsDebug) console.log('[analytics] enabled (awaiting consent):', { trackingId, consent: analyticsConsent });
  ensureGA4();
}

export async function mountEmbed(container: HTMLElement): Promise<void> {
  // Proactively push fresh credentials ~60s before the Okta token expires,
  // so the Proxima JWT is renewed before any API call sees a 401.
  userManager.events.addAccessTokenExpiring(async () => {
    const fresh = await silentRefresh();
    if (fresh) await setCredentials(identify, { token: fresh });
  });

  onLoginRequestedByExperience(identify, async () => {
    // Only attempt silent refresh if the user has a refresh token in storage.
    // Without one, signinSilent falls back to iframe renew which waits up to 10s before
    // failing — causing the login button to appear frozen before the Okta redirect.
    const user = await userManager.getUser();
    if (user?.refresh_token) {
      const fresh = await Promise.race([
        silentRefresh(),
        new Promise<null>(res => setTimeout(() => res(null), 3000)),
      ]);
      if (fresh) {
        await setCredentials(identify, { token: fresh });
        return;
      }
    }
    await login();
  });

  await sdkEmbed(experience, container);

  // When analytics consent changes, (re)evaluate whether GA4 should be loaded.
  onConsentChange(prefs => { analyticsConsent = prefs.analytics; ensureGA4(); });

  onMessage(experience, ({ action, payload }) => {
    // The embed asks the parent to open the OneTrust preference centre (the embed
    // cannot reach OneTrust itself — it lives on the parent document).
    if (action === 'proxima:open-consent') {
      openPreferences();
      return;
    }
    // Forward app analytics events to GA4, but only once it is initialised
    // (which only happens after analytics consent is granted).
    if (action === 'track' && ga4Initialized && analyticsConsent) {
      const { event, category, ...dims } = payload as { event: string; category: string; [key: string]: unknown };
      if (analyticsDebug) console.log('[analytics] event:', event, { event_category: category, ...dims });
      window.gtag('event', event, { event_category: category, ...dims });
    }
  });
}

export async function sendToken(accessToken: string): Promise<void> {
  await setCredentials(identify, { token: accessToken });
}

export function sendConsent(categories: ConsentCategory[]): void {
  sendMessage(experience, 'proxima:consent', { categories });
}

export function unmountEmbed(container: HTMLElement): void {
  sdkUnmount(container);
}
